import React, { useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Stack,
    Avatar,
    Button,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
    Divider,
    ListItemIcon,
} from '@mui/material';
import {
    MoreVert,
    Block,
    LockOpen,
    DeleteOutline,
    Email,
    Phone,
    ContentCopyOutlined,
    Today,
    AccessTime,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import CustomerStatusBadge from '../Table/CustomerStatusBadge';
import { useUpdateCustomerStatus } from '../../../hooks/customers/useUpdateCustomerStatus';
import { useDeleteCustomer } from '../../../hooks/customers/useDeleteCustomer';

dayjs.extend(relativeTime);

const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
        toast.success(`${label}: ${text}`);
    });
};

const InfoLine = ({ icon: Icon, value, copyable, copyLabel, ltr }) =>
    value ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Icon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    direction: ltr ? 'ltr' : undefined,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                }}
            >
                {value}
            </Typography>
            {copyable && (
                <Tooltip title={copyLabel}>
                    <IconButton
                        size="small"
                        onClick={() => copy(value, copyLabel)}
                        sx={{ p: 0.25, flexShrink: 0 }}
                    >
                        <ContentCopyOutlined sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    ) : null;

const CustomerHeader = ({ customer, onDeleted }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [menuAnchor, setMenuAnchor] = useState(null);
    const { updateCustomerStatusMutate, isUpdatingStatus } = useUpdateCustomerStatus();
    const { deleteCustomerMutate, isDeleting } = useDeleteCustomer();

    if (!customer) return null;

    const isBanned = customer.status === 'banned';
    const initials =
        (customer.name || '')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0])
            .join('')
            .toUpperCase() || '?';

    const stats = customer.stats || {};
    const lastOrderAt = stats.last_order_at || customer.last_order_at;

    const toggleStatus = () => {
        updateCustomerStatusMutate({
            id: customer.id,
            data: { status: isBanned ? 'active' : 'banned' },
        });
        setMenuAnchor(null);
    };

    const handleDelete = () => {
        const ok = window.confirm(
            isArabic
                ? 'سيتم حذف العميل (soft delete). هل أنت متأكد؟'
                : 'Customer will be soft-deleted. Are you sure?'
        );
        if (!ok) return;
        deleteCustomerMutate(customer.id, {
            onSuccess: () => onDeleted?.(),
        });
        setMenuAnchor(null);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
                background: (t) =>
                    t.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${t.palette.background.paper} 0%, ${t.palette.primary.main}11 100%)`
                        : `linear-gradient(135deg, #fff 0%, ${t.palette.primary.main}08 100%)`,
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2.5}
                alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                justifyContent="space-between"
            >
                {/* Avatar + identity */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ minWidth: 0, flex: 1 }}
                >
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            fontSize: 26,
                            bgcolor: 'primary.main',
                            boxShadow: 2,
                            flexShrink: 0,
                        }}
                    >
                        {initials}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1, width: '100%' }}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            mb={1}
                            flexWrap="wrap"
                            sx={{ rowGap: 0.75 }}
                        >
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}
                            >
                                {customer.name || '-'}
                            </Typography>
                            <CustomerStatusBadge status={customer.status} />
                        </Stack>

                        {/* Compact info grid — wraps cleanly */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: 'minmax(0, auto) minmax(0, auto)',
                                },
                                columnGap: 3,
                                rowGap: 0.75,
                            }}
                        >
                            <InfoLine
                                icon={Email}
                                value={customer.email}
                                copyable
                                copyLabel={isArabic ? 'تم نسخ البريد' : 'Email copied'}
                                ltr
                            />
                            <InfoLine
                                icon={Phone}
                                value={customer.phone}
                                copyable
                                copyLabel={isArabic ? 'تم نسخ الهاتف' : 'Phone copied'}
                                ltr
                            />
                            <InfoLine
                                icon={Today}
                                value={
                                    customer.created_at
                                        ? `${isArabic ? 'انضم' : 'Joined'} ${dayjs(customer.created_at).format('YYYY-MM-DD')}`
                                        : null
                                }
                            />
                            <InfoLine
                                icon={AccessTime}
                                value={
                                    lastOrderAt
                                        ? `${isArabic ? 'آخر طلب' : 'Last order'} ${dayjs(lastOrderAt).fromNow()}`
                                        : null
                                }
                            />
                        </Box>
                    </Box>
                </Stack>

                {/* Actions */}
                <Box sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', md: 'flex-start' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<MoreVert />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={isUpdatingStatus || isDeleting}
                        fullWidth
                        sx={{ borderRadius: 2, fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    >
                        {isArabic ? 'إجراءات' : 'Actions'}
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: isArabic ? 'left' : 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: isArabic ? 'left' : 'right' }}
                    >
                        <MenuItem onClick={toggleStatus} disabled={isUpdatingStatus}>
                            <ListItemIcon>
                                {isBanned ? (
                                    <LockOpen fontSize="small" color="success" />
                                ) : (
                                    <Block fontSize="small" color="error" />
                                )}
                            </ListItemIcon>
                            {isBanned
                                ? (isArabic ? 'إلغاء الحظر' : 'Unban customer')
                                : (isArabic ? 'حظر العميل' : 'Ban customer')}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleDelete} disabled={isDeleting} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteOutline fontSize="small" color="error" />
                            </ListItemIcon>
                            {isArabic ? 'حذف' : 'Delete'}
                        </MenuItem>
                    </Menu>
                </Box>
            </Stack>
        </Paper>
    );
};

export default CustomerHeader;
